import {
  keepPreviousData,
  QueryKey,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import axiosDefault from "axios";
import { useCallback, useEffect, useMemo } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { loadingState, tokenState, userState } from "store/store";
import { TErrCodes } from "types/types";

// export const isDev = process.env.NODE_ENV === "development";
export const isDev = false;

const DEFAULT_CONFIG = {
  baseURL: isDev
    ? "http://localhost:3000/api"
    : "https://www.victoryqna.com/api",
  headers: {
    "Content-Type": "application/json",
  },
};

export const axios = axiosDefault.create(DEFAULT_CONFIG);

const useAxios = () => {
  const [token, setToken] = useRecoilState(tokenState);
  const setUser = useSetRecoilState(userState);
  const queryClient = useQueryClient();

  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        if (error?.response?.status === 401) {
          if (token) {
            axios.post("v1/auth/logout").then(() => {});
            setUser(null);
            setToken(null);
            localStorage.removeItem("lastActivity");
          } else {
            setUser(null);
            setToken(null);
            localStorage.removeItem("lastActivity");
          }
        } else if (error?.response?.status === 403) {
          queryClient.invalidateQueries({ queryKey: ["auth/this"] });
        } else {
          throw error;
        }
      },
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [token]);

  useEffect(() => {
    axios.defaults.headers.common.Authorization = `Bearer ${token || ""}`;
  }, [token]);
};

export const useFetchData = <T>({
  endpoint,
  queryKey,
  showLoading = true,
  meta,
  params,
  keepPreviousData: keepPrevious,
  onSuccess,
  invalidateQueries,
  staleTime,
  placeholderData,
  enabled,
}: {
  endpoint: string;
  queryKey: Array<string | number>;
  showLoading?: boolean;
  meta?: { errCode?: TErrCodes; errMsg?: Record<number, string> };
  params?: any;
  keepPreviousData?: boolean;
  onSuccess?: (data: T) => void;
  invalidateQueries?: QueryKey[];
  staleTime?: number;
  placeholderData?: T;
  enabled?: boolean;
}) => {
  const setLoading = useSetRecoilState(loadingState);
  const queryClient = useQueryClient();
  const token = useRecoilValue(tokenState);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get<T | undefined>(endpoint, {
        params,
        headers: {
          Authorization: `Bearer ${token || ""}`,
        },
      });
      onSuccess?.(res.data!);
      invalidateQueries?.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
      return res.data;
    } catch (e: any) {
      throw new Error(e);
    } finally {
      showLoading && setLoading(false);
    }
  }, [
    endpoint,
    setLoading,
    showLoading,
    params,
    invalidateQueries,
    queryClient,
    token,
  ]);

  const { isFetching, isRefetching, ...rest } = useQuery({
    queryKey,
    queryFn: fetchData,
    meta,
    // @ts-expect-error
    placeholderData: keepPrevious ? keepPreviousData : placeholderData,
    staleTime,
    enabled,
  });

  useEffect(() => {
    if (isFetching && !isRefetching && showLoading) {
      setLoading(true);
    }
  }, [isFetching, isRefetching]);

  return { isFetching, isRefetching, ...rest };
};
export const useFetchPaginatedData = <T>({
  endpoint,
  queryKey,
  showLoading = true,
  meta,
  params,
  keepPreviousData: keepPrevious,
  onSuccess,
  invalidateQueries,
  staleTime,
  placeholderData,
  enabled,
}: {
  endpoint: string;
  queryKey: Array<string | number>;
  showLoading?: boolean;
  meta?: { errCode?: TErrCodes };
  params?: any;
  keepPreviousData?: boolean;
  onSuccess?: (data: T[]) => void;
  invalidateQueries?: QueryKey[];
  staleTime?: number;
  placeholderData?: T;
  enabled?: boolean;
}) => {
  const setLoading = useSetRecoilState(loadingState);
  const queryClient = useQueryClient();
  const token = useRecoilValue(tokenState);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get<{
        data: T[] | undefined;
        totalItems: number;
        totalPages: number;
        currentPage: number;
      }>(endpoint, {
        params,
        headers: {
          Authorization: `Bearer ${token || ""}`,
        },
      });
      onSuccess?.(res.data.data!);
      invalidateQueries?.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
      return res.data;
    } catch (e: any) {
      throw new Error(e);
    } finally {
      showLoading && setLoading(false);
    }
  }, [endpoint, showLoading, params, invalidateQueries, queryClient, token]);

  const { isFetching, isRefetching, data, ...rest } = useQuery({
    queryKey,
    queryFn: fetchData,
    meta,
    // @ts-expect-error
    placeholderData: keepPrevious ? keepPreviousData : placeholderData,
    staleTime,
    enabled,
  });

  useEffect(() => {
    if (isFetching && !isRefetching && showLoading) {
      setLoading(true);
    }
  }, [isFetching, isRefetching]);

  return {
    isFetching,
    isRefetching,
    ...rest,
    ...data,
  };
};

export const useGenericMutation = <T = void, V = Record<string, void>>({
  endpoint,
  showLoading = true,
  invalidateQueries,
  invalidateQueriesOnError,
  onSuccess,
  onError,
  meta,
  method = "post",
  dataTransform = (data) => data,
  contentType,
  endLoading = true,
}: {
  endpoint: string | ((data: T) => string);
  showLoading?: boolean | ((data: T) => boolean);
  invalidateQueries?:
    | QueryKey[]
    | ((data: V | undefined, variables: T) => QueryKey[]);
  invalidateQueriesOnError?: boolean;
  onSuccess?: (data: V, variables: T) => void;
  onError?: (e: any) => void;
  meta?: { errCode?: TErrCodes; errMsg?: Record<number, string> };
  method?: "get" | "post" | "put" | "delete";
  dataTransform?: (data: T) => any;
  contentType?: string;
  endLoading?: boolean | ((data?: V) => boolean);
}) => {
  const setLoading = useSetRecoilState(loadingState);
  const queryClient = useQueryClient();
  const token = useRecoilValue(tokenState);

  const headers = {
    Authorization: `Bearer ${token || ""}`,
    ...(contentType ? { "Content-Type": contentType } : {}),
  };

  return useMutation({
    mutationFn: async (form: T) => {
      const endPointValue =
        typeof endpoint === "string" ? endpoint : endpoint(form);
      const axiosMethods = {
        get: () => axios.get<V>(endPointValue, { headers }),
        post: (data: T) => axios.post<V>(endPointValue, data, { headers }),
        delete: () => axios.delete<V>(endPointValue, { headers }),
        put: (data: T) => axios.put<V>(endPointValue, data, { headers }),
      };

      const axiosMethod = axiosMethods[method];
      const response = await axiosMethod(dataTransform(form));
      return response?.data;
    },
    onMutate: (data) => {
      const isShowLoading =
        typeof showLoading === "boolean" ? showLoading : showLoading(data);
      isShowLoading && setLoading(true);
    },
    onSettled: (req, __, data) => {
      const isShowLoading =
        typeof showLoading === "boolean" ? showLoading : showLoading(data);
      const isEndLoading =
        typeof endLoading === "boolean" ? endLoading : endLoading(req);
      isShowLoading && isEndLoading && setLoading(false);
    },
    onSuccess: (data, variables) => {
      onSuccess?.(data, variables);
      if (invalidateQueries) {
        if (Array.isArray(invalidateQueries)) {
          invalidateQueries?.forEach((key) => {
            queryClient.invalidateQueries({ queryKey: key });
          });
        } else {
          invalidateQueries(data, variables)?.forEach((key) => {
            queryClient.invalidateQueries({ queryKey: key });
          });
        }
      }
    },
    onError: (data, variables) => {
      onError?.(data);
      if (invalidateQueries && invalidateQueriesOnError) {
        if (Array.isArray(invalidateQueries)) {
          invalidateQueries?.forEach((key) => {
            queryClient.invalidateQueries({ queryKey: key });
          });
        } else {
          invalidateQueries(undefined, variables)?.forEach((key) => {
            queryClient.invalidateQueries({ queryKey: key });
          });
        }
      }
    },
    meta,
  });
};

export const useFetchInfiniteData = <T>({
  endpoint,
  queryKey,
  showLoading = true,
  meta,
  params,
  onSuccess,
  enabled,
}: {
  endpoint: string;
  queryKey: Array<string | number>;
  showLoading?: boolean;
  meta?: { errCode?: TErrCodes };
  params?: any;
  keepPreviousData?: boolean;
  onSuccess?: (data: {
    data: T[];
    totalItems: number;
    totalPages: number;
  }) => void;
  enabled?: boolean;
}) => {
  const token = useRecoilValue(tokenState);
  const setLoading = useSetRecoilState(loadingState);

  const fetchData = useCallback(
    async ({ pageParam = 1 }) => {
      try {
        const res = await axios.get<{
          data: T[];
          totalItems: number;
          totalPages: number;
          currentPage: number;
        }>(endpoint, {
          params: { page: pageParam, ...(params || {}) },
          headers: {
            Authorization: `Bearer ${token || ""}`,
          },
        });
        onSuccess?.(res.data);
        return res.data;
      } catch (e: any) {
        console.log(e);
        throw new Error(e);
      } finally {
        showLoading && setLoading(false);
      }
    },
    [endpoint, setLoading, showLoading, params, token],
  );

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isRefetching,
    ...rest
  } = useInfiniteQuery({
    queryKey,
    queryFn: fetchData,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      return pages.length < lastPage.totalPages ? pages.length + 1 : undefined;
    },
    meta,
    enabled,
  });

  const dataFlat = useMemo(
    () => data?.pages?.map((page) => page.data)?.flat() || [],
    [data],
  );

  const loadMore = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage]);

  return {
    data: dataFlat as T[] | undefined,
    loadMore,
    isFetching,
    isRefetching,
    totalItems: data?.pages?.[0]?.totalItems ?? 0,
    hasNextPage,
    currentPage: data?.pages?.[0]?.currentPage ?? 1,
    ...rest,
  };
};

export default useAxios;
